/**
 * 3D Perspektif Okey Masası
 * Oyuncunun bakış açısından - gerçekçi kıraathane hissi
 */
import { useState } from "react";
import { cn } from "../../lib/utils";
import OkeyTile from "./OkeyTile";
import { Button } from "../ui/button";
import { Clock, Volume2, VolumeX, Layers, ArrowUpDown, Undo2, Play, Trash2 } from "lucide-react";

// Taş boyutları
const TILE_WIDTH = 44;
const TILE_HEIGHT = 60;
const SLOT_WIDTH = 48;
const GAP_WIDTH = SLOT_WIDTH;

export default function OkeyTable3D({
  gameState,
  currentUserId,
  onDrawCenter,
  onDrawLeft,
  onDiscard,
  onOpenHand,
  selectedTiles,
  onTileSelect,
  turnTimeRemaining,
  // Yeni props
  localTiles,
  onSortBySeries,
  onSortByPairs,
  onResetTiles,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  dragOverIndex,
  onOpenSeries,
  onOpenPairs,
  // Akıllı yardım props
  smartHelpInfo,
  selectedTilesValid
}) {
  const [soundEnabled, setSoundEnabled] = useState(true);

  if (!gameState) return null;

  const { hands, discard_piles, center_pile_count, current_player_id, scores, indicator_tile, okey_tile, turn_number } = gameState;
  
  const playerOrder = gameState.game?.player_ids || Object.keys(hands || {});
  const myIndex = playerOrder.indexOf(currentUserId);
  
  const myHand = hands?.[currentUserId];
  const isMyTurn = current_player_id === currentUserId;
  const myTiles = myHand?.tiles || [];
  
  // 101 Okey kuralı: İlk oyuncu 22 taşla başlar ve direkt taş atar (çekmez)
  // 22 taş varsa zaten çekmiş sayılır
  const tileCount = myHand?.tile_count || myTiles.length;
  const mustDiscard = tileCount === 22; // 22 taş varsa direkt atmalı

  // Karşı oyuncuları hesapla
  const getOpponent = (offset) => {
    const index = (myIndex + offset) % 4;
    return playerOrder[index];
  };

  const leftOpponent = getOpponent(1);
  const topOpponent = getOpponent(2);
  const rightOpponent = getOpponent(3);

  return (
    <div className="relative w-full h-full min-h-[700px] overflow-hidden">
      {/* Arka plan - Kıraathane ambiyansı */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse at center bottom, #2d1810 0%, #1a0f0a 50%, #0d0705 100%)
          `
        }}
      />
      
      {/* Tavan ışığı efekti */}
      <div 
        className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 opacity-30"
        style={{
          background: `radial-gradient(circle, rgba(255,200,100,0.3) 0%, transparent 70%)`
        }}
      />

      {/* 3D Masa - Perspektif */}
      <div 
        className="absolute inset-0 flex items-center justify-center"
        style={{ perspective: "1200px" }}
      >
        <div 
          className="relative w-[800px] h-[500px]"
          style={{
            transform: "rotateX(45deg)",
            transformStyle: "preserve-3d"
          }}
        >
          {/* Masa yüzeyi */}
          <div 
            className="absolute inset-0 rounded-3xl shadow-2xl"
            style={{
              background: `
                linear-gradient(180deg, 
                  #1a472a 0%, 
                  #2d5a3d 30%,
                  #1a472a 100%
                )
              `,
              boxShadow: `
                0 20px 60px rgba(0,0,0,0.8),
                inset 0 0 100px rgba(0,0,0,0.3),
                inset 0 0 20px rgba(255,255,255,0.05)
              `,
              border: "12px solid #5C3317"
            }}
          >
            {/* Masa kenarı - ahşap */}
            <div 
              className="absolute -inset-3 rounded-3xl -z-10"
              style={{
                background: `linear-gradient(180deg, #8B4513 0%, #5C3317 50%, #3D2314 100%)`,
                boxShadow: "0 10px 30px rgba(0,0,0,0.5)"
              }}
            />
          </div>

          {/* Orta alan */}
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-4"
            style={{ transform: "translateZ(20px)", zIndex: 20 }}
          >
            {/* Gösterge */}
            <div className="flex items-center gap-3 bg-black/40 px-4 py-2 rounded-xl backdrop-blur-sm">
              <span className="text-white/60 text-xs">Gösterge:</span>
              {indicator_tile && <OkeyTile tile={indicator_tile} size="xs" />}
            </div>

            {/* Taş destesi */}
            <button
              onClick={onDrawCenter}
              disabled={!isMyTurn || mustDiscard}
              className={cn(
                "relative w-16 h-24 rounded-lg transition-all",
                "bg-gradient-to-br from-[#8B4513] via-[#A0522D] to-[#6B3E26]",
                isMyTurn && !mustDiscard && "ring-2 ring-[#4DFFFF] animate-pulse cursor-pointer hover:scale-110",
                (!isMyTurn || mustDiscard) && "opacity-60"
              )}
              style={{
                boxShadow: "0 8px 20px rgba(0,0,0,0.5)",
                transform: "translateZ(10px)"
              }}
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                <span className="text-xl">☕</span>
                <span className="text-[10px] opacity-70">{center_pile_count}</span>
              </div>
            </button>
            
            {/* Kalan taş sayısı - daha belirgin */}
            <div className="bg-black/60 px-3 py-1 rounded-lg text-white font-bold text-sm shadow-lg border border-white/20">
              Kalan: {center_pile_count} taş
            </div>
          </div>

          {/* Karşı oyuncu (üst) */}
          <OpponentArea3D
            playerId={topOpponent}
            hand={hands?.[topOpponent]}
            discardPile={discard_piles?.[topOpponent]}
            score={scores?.[topOpponent] || 0}
            isCurrentPlayer={current_player_id === topOpponent}
            position="top"
            onDrawLeft={onDrawLeft}
          />

          {/* Sol oyuncu */}
          <OpponentArea3D
            playerId={leftOpponent}
            hand={hands?.[leftOpponent]}
            discardPile={discard_piles?.[leftOpponent]}
            score={scores?.[leftOpponent] || 0}
            isCurrentPlayer={current_player_id === leftOpponent}
            position="left"
            onDrawLeft={onDrawLeft}
          />

          {/* Sağ oyuncu */}
          <OpponentArea3D
            playerId={rightOpponent}
            hand={hands?.[rightOpponent]}
            discardPile={discard_piles?.[rightOpponent]}
            score={scores?.[rightOpponent] || 0}
            isCurrentPlayer={current_player_id === rightOpponent}
            position="right"
            onDrawLeft={onDrawLeft}
          />
        </div>
      </div>

      {/* Benim elim (altta, 3D dışında) */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent pt-8 pb-4">
        {/* Seçili Taş Durumu */}
        {selectedTiles?.length > 0 && selectedTilesValid && (
          <div className="flex justify-center mb-2">
            <div className={cn(
              "px-3 py-1 rounded text-sm",
              selectedTilesValid.valid 
                ? "bg-green-500/20 text-green-400" 
                : "bg-red-500/20 text-red-400"
            )}>
              {selectedTilesValid.valid 
                ? `✓ Geçerli ${selectedTilesValid.type === 'seri' ? 'Seri' : 'Per'} (${selectedTilesValid.points} puan)`
                : `✗ Geçersiz kombinasyon (${selectedTiles.length} taş seçili)`
              }
            </div>
          </div>
        )}

        {/* Istaka - Kendi taşlarım (Gerçekçi Ahşap Istaka) */}
        <div className="flex justify-center">
          <WoodenRack3D
            tiles={localTiles?.length > 0 ? localTiles : myTiles}
            selectedTiles={selectedTiles}
            onTileSelect={onTileSelect}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
            dragOverIndex={dragOverIndex}
            onSortBySeries={onSortBySeries}
            onSortByPairs={onSortByPairs}
            onResetTiles={onResetTiles}
            smartHelpInfo={smartHelpInfo}
            isMyTurn={isMyTurn}
            score={scores?.[currentUserId] || 0}
          />
        </div>

        {/* Açılan perlerim */}
        {myHand?.opened_pers?.length > 0 && (
          <div className="flex justify-center gap-4 mt-4">
            {myHand.opened_pers.map((per, i) => (
              <div 
                key={i} 
                className="flex gap-1 bg-black/30 p-2 rounded-lg"
              >
                {per.tiles.map((tile, j) => (
                  <OkeyTile key={j} tile={tile} size="sm" />
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Alt Aksiyon Butonları */}
        <div className="flex justify-center gap-3 mt-4">
          {isMyTurn && (
            <>
              {/* Taş At */}
              {selectedTiles?.length === 1 && (
                <Button
                  onClick={() => onDiscard?.(selectedTiles[0])}
                  className="bg-red-500 hover:bg-red-600 text-white font-bold px-6"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  TAŞ AT
                </Button>
              )}
              
              {/* Per Açma Butonları */}
              {selectedTiles?.length >= 3 && (
                <>
                  <Button
                    onClick={onOpenSeries}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-6"
                  >
                    <Play className="w-4 h-4 mr-1" />
                    DÜZAÇ ({selectedTiles.length})
                  </Button>
                  <Button
                    onClick={onOpenPairs}
                    className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-6"
                  >
                    <Layers className="w-4 h-4 mr-1" />
                    ÇİFT AÇ ({selectedTiles.length})
                  </Button>
                </>
              )}
            </>
          )}
          
          {/* Seçili taş sayısı */}
          {selectedTiles?.length > 0 && (
            <div className="text-white/70 flex items-center gap-2">
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                {selectedTiles.length} taş seçili
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Ses kontrolü */}
      <button
        onClick={() => setSoundEnabled(!soundEnabled)}
        className="absolute top-4 right-4 p-2 bg-black/30 rounded-lg text-white/60 hover:text-white transition-colors"
      >
        {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
      </button>
    </div>
  );
}

function OpponentArea3D({ playerId, hand, discardPile, score, isCurrentPlayer, position, onDrawLeft }) {
  const tileCount = hand?.tile_count || 0;
  const lastDiscard = discardPile?.[discardPile.length - 1];

  const positionStyles = {
    top: {
      container: "absolute top-8 left-1/2 -translate-x-1/2",
      tiles: "flex gap-0.5",
      info: "mb-2"
    },
    left: {
      container: "absolute left-8 top-1/2 -translate-y-1/2",
      tiles: "flex flex-col gap-0.5",
      info: "mb-2"
    },
    right: {
      container: "absolute right-8 top-1/2 -translate-y-1/2",
      tiles: "flex flex-col gap-0.5",
      info: "mb-2"
    }
  };

  const style = positionStyles[position];

  return (
    <div className={style.container} style={{ transform: "translateZ(30px)" }}>
      {/* Oyuncu bilgisi */}
      <div className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-lg",
        style.info,
        isCurrentPlayer ? "bg-[#4DFFFF]/30 border border-[#4DFFFF]/50" : "bg-black/40"
      )}>
        <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold">
          {hand?.username?.[0]?.toUpperCase() || "?"}
        </div>
        <span className="text-white text-xs">{hand?.username || playerId?.slice(-4)}</span>
        <span className="text-[#C6A87C] text-xs font-bold">{score}</span>
        {isCurrentPlayer && (
          <div className="w-2 h-2 rounded-full bg-[#4DFFFF] animate-pulse" />
        )}
      </div>

      {/* Kapalı taşlar */}
      <div className={style.tiles}>
        {Array(Math.min(tileCount, 22)).fill(0).map((_, i) => (
          <OkeyTile
            key={i}
            tile={{ color: "back" }}
            size="xs"
            faceDown
          />
        ))}
      </div>

      {/* Son atılan taş */}
      {lastDiscard && (
        <button
          onClick={() => onDrawLeft?.(playerId)}
          className={cn(
            "mt-2 p-1 rounded transition-all",
            isCurrentPlayer && "ring-2 ring-yellow-400"
          )}
          title="Soldan çek"
        >
          <OkeyTile tile={lastDiscard} size="xs" />
        </button>
      )}

      {/* Açılan perler */}
      {hand?.opened_pers?.length > 0 && (
        <div className="flex gap-1 mt-2">
          {hand.opened_pers.map((per, i) => (
            <div key={i} className="flex gap-0.5 bg-black/20 p-0.5 rounded">
              {per.tiles.map((tile, j) => (
                <OkeyTile key={j} tile={tile} size="xs" />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Sabit 2 Katlı Ahşap Istaka - 3D Görünüm
 * - Üst sıra: 15 slot
 * - Alt sıra: 15 slot
 * - Taşlar gap'lerle birlikte 2 satıra akıllıca dağıtılır
 */
function WoodenRack3D({ 
  tiles, 
  selectedTiles, 
  onTileSelect, 
  onDragStart, 
  onDragOver, 
  onDrop, 
  onDragEnd, 
  dragOverIndex,
  onSortBySeries,
  onSortByPairs,
  onResetTiles,
  smartHelpInfo,
  isMyTurn,
  score
}) {
  const SLOTS_PER_ROW = 15;
  const RACK_WIDTH = 780;
  
  // Taşları 2 satıra akıllıca dağıt (gap'leri hesaba katarak)
  const topRowTiles = [];
  const bottomRowTiles = [];
  
  let currentRowWidth = 0;
  const maxRowWidth = SLOTS_PER_ROW * SLOT_WIDTH;
  
  tiles.forEach((tile, idx) => {
    const tileWidth = SLOT_WIDTH + (tile._gap ? GAP_WIDTH : 0);
    
    if (currentRowWidth + tileWidth <= maxRowWidth && topRowTiles.length < SLOTS_PER_ROW) {
      topRowTiles.push({ ...tile, originalIndex: idx });
      currentRowWidth += tileWidth;
    } else {
      bottomRowTiles.push({ ...tile, originalIndex: idx, _gap: bottomRowTiles.length === 0 ? false : tile._gap });
    }
  });

  return (
    <div 
      className="relative rounded-2xl overflow-hidden"
      style={{
        background: `linear-gradient(180deg, 
          #C9A66B 0%, #B8956A 5%, #A07850 15%, #8B6914 30%,
          #7A5C12 50%, #6B4E0A 70%, #5A4008 85%, #4A3508 100%
        )`,
        boxShadow: `
          0 -4px 20px rgba(0,0,0,0.5),
          0 8px 30px rgba(0,0,0,0.6),
          inset 0 2px 4px rgba(255,255,255,0.2),
          inset 0 -4px 8px rgba(0,0,0,0.4)
        `,
        border: "4px solid #3D2906",
        width: `${RACK_WIDTH}px`
      }}
    >
      {/* Ahşap damar deseni */}
      <div 
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage: `
            repeating-linear-gradient(90deg, transparent, transparent 30px, rgba(0,0,0,0.1) 30px, rgba(0,0,0,0.1) 32px),
            repeating-linear-gradient(0deg, transparent, transparent 60px, rgba(139,105,20,0.2) 60px, rgba(139,105,20,0.2) 62px)
          `
        }}
      />

      {/* Kontrol Butonları, Akıllı Yardım ve Sıra Sizde - Tek satırda */}
      <div className="flex justify-center items-center gap-2 pt-3 pb-2 relative z-10 flex-wrap">
        <Button
          onClick={onSortBySeries}
          size="sm"
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg"
        >
          <Layers className="w-4 h-4 mr-1" />
          SERİ DİZ
        </Button>
        <Button
          onClick={onSortByPairs}
          size="sm"
          className="bg-green-600 hover:bg-green-700 text-white font-bold shadow-lg"
        >
          <ArrowUpDown className="w-4 h-4 mr-1" />
          ÇİFT DİZ
        </Button>
        <Button
          onClick={onResetTiles}
          size="sm"
          variant="outline"
          className="border-white/50 text-white hover:bg-white/20 shadow-lg"
        >
          <Undo2 className="w-4 h-4 mr-1" />
          GERİ AL
        </Button>
        
        {/* Akıllı Yardım */}
        {smartHelpInfo && (
          <div className={cn(
            "px-3 py-1 rounded-lg font-bold text-xs",
            smartHelpInfo.type === 'can_open_101' && "bg-green-500/40 text-green-300 border border-green-500/50",
            smartHelpInfo.type === 'can_open_5_pairs' && "bg-purple-500/40 text-purple-300 border border-purple-500/50",
            smartHelpInfo.type === 'cannot_open' && "bg-black/40 text-white/80",
            smartHelpInfo.type === 'opened' && "bg-yellow-500/40 text-yellow-300"
          )}>
            {smartHelpInfo.type === 'can_open_101' && `✨ ${smartHelpInfo.points} puan`}
            {smartHelpInfo.type === 'can_open_5_pairs' && `🎯 5 Çift!`}
            {smartHelpInfo.type === 'cannot_open' && `📊 ${smartHelpInfo.points}/101 | ${smartHelpInfo.pairCount || 0} çift`}
            {smartHelpInfo.type === 'opened' && `✓ El Açık`}
          </div>
        )}
        
        {/* Puan */}
        <div className="text-[#C6A87C] font-bold text-sm px-2">
          {score} puan
        </div>
        
        {/* Sıra Sizde - En sonda */}
        {isMyTurn && (
          <div className="text-[#4DFFFF] font-bold animate-pulse text-sm px-2 border-l border-white/30">
            ⚡ Sıra Sizde!
          </div>
        )}
      </div>

      {/* Üst Sıra */}
      <RackRow3D 
        tiles={topRowTiles}
        rowOffset={0}
        slotsPerRow={SLOTS_PER_ROW}
        selectedTiles={selectedTiles}
        onTileSelect={onTileSelect}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
        dragOverIndex={dragOverIndex}
      />

      {/* Alt Sıra */}
      <RackRow3D 
        tiles={bottomRowTiles}
        rowOffset={topRowTiles.length}
        slotsPerRow={SLOTS_PER_ROW}
        selectedTiles={selectedTiles}
        onTileSelect={onTileSelect}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
        dragOverIndex={dragOverIndex}
      />

      <div className="h-2" />
    </div>
  );
}

/**
 * Istaka Satırı - 3D
 * Gap varsa taşın solunda boşluk
 */
function RackRow3D({
  tiles,
  rowOffset,
  slotsPerRow,
  selectedTiles,
  onTileSelect,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  dragOverIndex
}) {
  const TOTAL_WIDTH = slotsPerRow * SLOT_WIDTH;
  
  return (
    <div 
      className="flex items-end justify-center my-1 rounded-lg"
      style={{ 
        minHeight: "64px",
        background: "rgba(0,0,0,0.2)",
        padding: "4px 8px",
        marginLeft: "8px",
        marginRight: "8px"
      }}
    >
      {tiles.map((tile, slotIdx) => {
        if (!tile) return null;
        
        const actualIdx = tile.originalIndex !== undefined ? tile.originalIndex : rowOffset + slotIdx;
        const isDropTarget = dragOverIndex === actualIdx;
        const hasGap = tile?._gap;
        
        return (
          <div 
            key={tile.tile_id || `slot-${actualIdx}`}
            className={cn(
              "flex items-end justify-center transition-all flex-shrink-0",
              isDropTarget && "bg-[#4DFFFF]/30 rounded"
            )}
            style={{ 
              width: `${SLOT_WIDTH}px`,
              height: "60px",
              marginLeft: hasGap ? `${GAP_WIDTH}px` : "0"
            }}
            onDragOver={(e) => onDragOver?.(e, actualIdx)}
            onDrop={(e) => onDrop?.(e, actualIdx)}
          >
            <div
              draggable={!!onDragStart}
              onDragStart={() => onDragStart?.(tile, actualIdx)}
              onDragEnd={onDragEnd}
              className="cursor-grab active:cursor-grabbing"
            >
              <OkeyTile
                tile={tile}
                size="md"
                selected={selectedTiles?.includes(tile.tile_id)}
                onClick={() => onTileSelect?.(tile.tile_id)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
